import {
  createPresentationId,
  emptyPresentationExtension,
  type Highlight,
  type HighlightEmphasis,
  type PresentationBrandingMode,
  type PresentationGraphExtension,
  type PresentationTheme,
  type PresentationViewport,
  type StrategicNote,
} from './contracts';

/**
 * SPEC-011 plan §20/§22/§34 — pure CRUD over `PresentationGraphExtension`,
 * the same shape SPEC-009's `scenario-crud.ts` established for `simulation`:
 * every function takes the current extension and returns a new one, so the
 * editor store can apply it through the existing `commit()` undo/autosave
 * pipeline without new plumbing.
 */

export function updatePresentationMeta(
  extension: PresentationGraphExtension,
  patch: Partial<
    Pick<
      PresentationGraphExtension,
      'title' | 'subtitle' | 'companyName' | 'showMetrics' | 'coverEnabled'
    >
  >,
): PresentationGraphExtension {
  return { ...extension, ...patch };
}

export function setPresentationTheme(
  extension: PresentationGraphExtension,
  theme: PresentationTheme,
): PresentationGraphExtension {
  return { ...extension, theme };
}

export function setPresentationBranding(
  extension: PresentationGraphExtension,
  brandingMode: PresentationBrandingMode,
): PresentationGraphExtension {
  return { ...extension, brandingMode };
}

export function setPresentationLogoAsset(
  extension: PresentationGraphExtension,
  assetId: string | undefined,
): PresentationGraphExtension {
  return { ...extension, companyLogoAssetId: assetId };
}

export function setPresentationDefaultViewport(
  extension: PresentationGraphExtension,
  viewport: PresentationViewport,
): PresentationGraphExtension {
  return { ...extension, defaultViewport: viewport };
}

// --- Strategic notes ---------------------------------------------------------

export function createStrategicNote(
  extension: PresentationGraphExtension,
  input: { title: string; body?: string; nodeIds?: string[] },
): { extension: PresentationGraphExtension; note: StrategicNote } {
  const note: StrategicNote = {
    id: createPresentationId(),
    title: input.title,
    body: input.body ?? '',
    nodeIds: input.nodeIds ?? [],
    order: extension.strategicNotes.length,
    visible: true,
  };
  return { extension: { ...extension, strategicNotes: [...extension.strategicNotes, note] }, note };
}

export function updateStrategicNote(
  extension: PresentationGraphExtension,
  noteId: string,
  patch: Partial<Pick<StrategicNote, 'title' | 'body' | 'nodeIds' | 'visible'>>,
): PresentationGraphExtension {
  return {
    ...extension,
    strategicNotes: extension.strategicNotes.map((note) =>
      note.id === noteId ? { ...note, ...patch } : note,
    ),
  };
}

export function deleteStrategicNote(
  extension: PresentationGraphExtension,
  noteId: string,
): PresentationGraphExtension {
  return {
    ...extension,
    strategicNotes: extension.strategicNotes
      .filter((note) => note.id !== noteId)
      .map((note, index) => ({ ...note, order: index })),
    // A highlight may reference the deleted note; drop the dangling ref rather than leaving it stale.
    highlights: extension.highlights.map((highlight) =>
      highlight.noteId === noteId ? { ...highlight, noteId: undefined } : highlight,
    ),
  };
}

export function reorderStrategicNotes(
  extension: PresentationGraphExtension,
  orderedIds: string[],
): PresentationGraphExtension {
  const orderIndex = new Map(orderedIds.map((id, index) => [id, index]));
  return {
    ...extension,
    strategicNotes: [...extension.strategicNotes]
      .map((note) => ({ ...note, order: orderIndex.get(note.id) ?? note.order }))
      .sort((a, b) => a.order - b.order),
  };
}

// --- Highlights ---------------------------------------------------------------

export function createHighlight(
  extension: PresentationGraphExtension,
  input: {
    label: string;
    nodeIds?: string[];
    edgeIds?: string[];
    emphasis?: HighlightEmphasis;
    noteId?: string;
  },
): { extension: PresentationGraphExtension; highlight: Highlight } {
  const highlight: Highlight = {
    id: createPresentationId(),
    label: input.label,
    nodeIds: input.nodeIds ?? [],
    edgeIds: input.edgeIds ?? [],
    order: extension.highlights.length,
    emphasis: input.emphasis ?? 'normal',
    noteId: input.noteId,
  };
  return {
    extension: { ...extension, highlights: [...extension.highlights, highlight] },
    highlight,
  };
}

export function updateHighlight(
  extension: PresentationGraphExtension,
  highlightId: string,
  patch: Partial<Pick<Highlight, 'label' | 'nodeIds' | 'edgeIds' | 'emphasis' | 'noteId'>>,
): PresentationGraphExtension {
  return {
    ...extension,
    highlights: extension.highlights.map((highlight) =>
      highlight.id === highlightId ? { ...highlight, ...patch } : highlight,
    ),
  };
}

export function deleteHighlight(
  extension: PresentationGraphExtension,
  highlightId: string,
): PresentationGraphExtension {
  return {
    ...extension,
    highlights: extension.highlights
      .filter((highlight) => highlight.id !== highlightId)
      .map((highlight, index) => ({ ...highlight, order: index })),
  };
}

export function reorderHighlights(
  extension: PresentationGraphExtension,
  orderedIds: string[],
): PresentationGraphExtension {
  const orderIndex = new Map(orderedIds.map((id, index) => [id, index]));
  return {
    ...extension,
    highlights: [...extension.highlights]
      .map((highlight) => ({
        ...highlight,
        order: orderIndex.get(highlight.id) ?? highlight.order,
      }))
      .sort((a, b) => a.order - b.order),
  };
}

export { emptyPresentationExtension };
